import { httpsCallable } from 'firebase/functions';
import { functions } from './config';
import type { InviteOutcome } from '../context/AuthContext';
import type { OrgRole } from '../models/types';

/* Org membership is written server-side only.

   `orgId` and `role` on users/{uid} are what every Firestore rule authorizes against, and
   syncOrgClaims mirrors them into the Auth token that storage.rules trusts. While the client
   could write them, that token claim was ultimately a value the client had chosen. The
   Firestore rules now whitelist users/{uid} to email and displayName, so these two callables
   — running under the Admin SDK, which bypasses rules — are the only way membership changes.

   Keep the shapes here in sync with functions/src/index.ts. */

type AcceptInviteResult =
  | { status: 'applied'; orgId: string }
  | { status: 'none' }
  | { status: 'expired' }
  | {
      status: 'conflict';
      currentOrgId: string;
      invite: { id: string; email: string; orgId: string; role: OrgRole };
      blockedReason?: string;
    };

/** Accept the pending invite for the signed-in account's own verified address, if any.
 *  Pass `confirmSwitch` only after the user has agreed to leave their current org, together with
 *  the `inviteId` of the invite they were shown, so the server applies exactly that one. */
export async function callAcceptInvite(confirmSwitch = false, inviteId?: string): Promise<InviteOutcome> {
  const fn = httpsCallable<{ confirmSwitch: boolean; inviteId?: string }, AcceptInviteResult>(functions, 'acceptInvite');
  const { data } = await fn(inviteId ? { confirmSwitch, inviteId } : { confirmSwitch });

  if (data.status === 'conflict') {
    return {
      status: 'conflict',
      currentOrgId: data.currentOrgId,
      // createdAt is not returned; nothing consuming a conflict displays it.
      invite: { ...data.invite, createdAt: '' },
      ...(data.blockedReason ? { blockedReason: data.blockedReason } : {}),
    };
  }
  return data;
}

/** Create an org and become its admin, in one server-side batch. */
export async function callCreateOrganization(name: string, displayName?: string): Promise<string> {
  const fn = httpsCallable<{ name: string; displayName?: string }, { orgId: string }>(functions, 'createOrganization');
  const { data } = await fn({ name, displayName });
  return data.orgId;
}
